/** i18n helpers for sentiment / judge analysis UI */

export const normalizeLookupKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

const humanizeLabel = (value) => {
  const str = String(value || '').trim();
  if (!str) return '';

  if (/[_-]/.test(str)) {
    const normalized = str.replace(/[_-]/g, ' ');
    return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
  }

  if (str === str.toLowerCase()) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return str;
};

const humanizeTag = (value) => humanizeLabel(value);

/** Maps normalized raw values → canonical i18n key (MVP v2 + legacy + aliases). */
const INTENT_CANONICAL_ALIASES = {
  billing_issue: 'billing_issue',
  refund_request: 'refund_request',
  cancellation_request: 'cancellation_request',
  order_status: 'order_status',
  technical_issue: 'technical_issue',
  account_access: 'account_access',
  how_to: 'how_to',
  product_info: 'product_info',
  complaint: 'complaint',
  delivery_issue: 'delivery_issue',
  pricing_plan: 'pricing_plan',
  feature_request: 'feature_request',
  other: 'other',
  purchase_intent: 'purchase_intent',
  purchase_decision: 'purchase_intent',
  human_request: 'human_request',
  privacy_request: 'privacy_request',
  product_inquiry: 'product_inquiry',
  technical_support: 'technical_support',
  usage_help: 'usage_help',
  billing_payment: 'billing_payment',
  billing_and_payment: 'billing_payment',
  billing_inquiry: 'billing_payment',
  subscription_management: 'subscription_management',
  cancellation_churn: 'cancellation_churn',
  cancellation: 'cancellation_request',
  order_delivery: 'order_delivery',
  critical_safety: 'critical_safety',
  feedback: 'feedback',
  general_inquiry: 'general_inquiry',
  content_generation_help: 'product_inquiry',
  how_to_use: 'usage_help',
  output_quality_issue: 'technical_issue',
  account_management: 'account_access',
  subscription_change: 'subscription_management',
  integration_help: 'technical_issue',
  product_feedback: 'feedback',
  support_request: 'technical_issue',
  subscription_cancellation: 'cancellation_churn',
  password_reset: 'account_access',
  informacje_o_produkcie: 'product_info',
  jak_to_zrobic: 'how_to',
  wsparcie_techniczne: 'technical_support',
  platnosci_i_rozliczenia: 'billing_payment',
  inne: 'other',
  proba_o_zwrot: 'refund_request',
  prośba_o_zwrot: 'refund_request',
  proba_o_anulowanie: 'cancellation_request',
  prośba_o_anulowanie: 'cancellation_request',
  anulowanie: 'cancellation_request',
  reklamacja: 'complaint',
  status_zamowienia: 'order_status',
  plan_cenowy: 'pricing_plan',
  problem_techniczny: 'technical_issue',
  problem_z_dostawa: 'delivery_issue',
  problem_z_platnosciami: 'billing_issue'
};

const TOPIC_CANONICAL_ALIASES = {
  insufficient_context: 'insufficient_context',
  information_request: 'information_request',
  onboarding_or_how_to: 'onboarding_or_how_to',
  feature_or_capability_question: 'feature_or_capability_question',
  followup_required: 'followup_required',
  resolved_confirmation: 'resolved_confirmation',
  unresolved_case: 'unresolved_case',
  billing_or_payment: 'billing_or_payment',
  billing_and_payment: 'billing_or_payment',
  order_or_delivery: 'order_or_delivery',
  exit_intent_signal: 'exit_intent_signal',
  order_status: 'order_status',
  order_delivery: 'order_or_delivery',
  product_info: 'product_info',
  pricing_plan: 'pricing_plan',
  how_to: 'how_to',
  refund_or_cancellation: 'refund_or_cancellation',
  troubleshooting_needed: 'troubleshooting_needed',
  account_access_issue: 'account_access_issue',
  complaint_or_dissatisfaction: 'complaint_or_dissatisfaction',
  policy_or_compliance: 'policy_or_compliance',
  no_progress_loop: 'no_progress_loop',
  escalation_or_handoff: 'escalation_or_handoff'
};

const resolveCanonicalKey = (value, aliasMap) => {
  const normalized = normalizeLookupKey(value);
  return aliasMap[normalized] || normalized;
};

export const translateKey = (t, key, fallbackValue, humanize = humanizeLabel) => {
  const translated = t(key);
  return translated !== key ? translated : humanize(fallbackValue || key.split('.').pop());
};

const translateFromNamespaces = (t, namespaces, fallbackValue) => {
  for (const key of namespaces) {
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return humanizeLabel(fallbackValue);
};

export const formatClassificationLabel = (value, t) => {
  const lookupKey = resolveCanonicalKey(value, TOPIC_CANONICAL_ALIASES);
  return translateKey(t, `assistant.classification.${lookupKey}`, value);
};

export const formatIntentLabel = (value, t) => {
  if (!value) return value;
  const canonicalKey = resolveCanonicalKey(value, INTENT_CANONICAL_ALIASES);
  return translateFromNamespaces(
    t,
    [
      `assistant.intents.${canonicalKey}`,
      `widgets.config.statistics.overview.intents.${canonicalKey}`
    ],
    canonicalKey
  );
};

export const formatTopicLabel = (value, t) => {
  if (!value) return value;
  const canonicalKey = resolveCanonicalKey(value, TOPIC_CANONICAL_ALIASES);
  return translateFromNamespaces(
    t,
    [
      `widgets.config.statistics.overview.topics.${canonicalKey}`,
      `assistant.classification.${canonicalKey}`
    ],
    canonicalKey
  );
};

const RESOLUTION_STATUS_ALIASES = {
  resolved: 'resolved',
  partially_resolved: 'partially_resolved',
  partial: 'partially_resolved',
  unresolved: 'unresolved'
};

export const formatResolutionStatusLabel = (status, t) => {
  if (!status) return status;
  const canonicalKey = RESOLUTION_STATUS_ALIASES[normalizeLookupKey(status)] || normalizeLookupKey(status);
  const widgetKey = canonicalKey === 'partially_resolved' ? 'partial' : canonicalKey;
  return translateFromNamespaces(
    t,
    [
      `assistant.analysis.resolutionStatus.${canonicalKey}`,
      `widgets.config.statistics.overview.resolution.${widgetKey}`
    ],
    canonicalKey
  );
};

export const formatSentimentLabel = (value, t) => {
  const lookupKey = normalizeLookupKey(value);
  return translateKey(t, `assistant.sentiment.${lookupKey}`, value);
};

export const formatTagLabel = (value, t) => {
  const lookupKey = normalizeLookupKey(value);
  return translateKey(t, `assistant.tags.${lookupKey}`, value, humanizeTag);
};

export const getRecommendedActionLabel = (value, t) =>
  translateKey(t, `assistant.recommendedActions.${value}`, value);

export const getSessionActionLabel = (value, t) =>
  translateKey(t, `assistant.sessionActions.${value}`, value);

export const getSystemActionLabel = (value, t) =>
  translateKey(t, `assistant.systemActions.${String(value).replace(/:/g, '_')}`, value);

export const getPriorityReasonLabel = (code, t) =>
  translateKey(t, `assistant.priorityReasons.${code}`, code);

export const getPriorityBands = (t) => [
  { key: 'P3', label: t('assistant.priority.bands.low'), color: '#10b981' },
  { key: 'P2', label: t('assistant.priority.bands.medium'), color: '#60a5fa' },
  { key: 'P1', label: t('assistant.priority.bands.high'), color: '#f97316' },
  { key: 'P0', label: t('assistant.priority.bands.critical'), color: '#ef4444' }
];

export const getEffortBands = (t) => [
  { key: '1', label: t('assistant.effort.bands.effortless'), color: '#10b981' },
  { key: '2', label: t('assistant.effort.bands.easy'), color: '#60a5fa' },
  { key: '3', label: t('assistant.effort.bands.moderate'), color: '#eab308' },
  { key: '4', label: t('assistant.effort.bands.hard'), color: '#f97316' },
  { key: '5', label: t('assistant.effort.bands.veryHard'), color: '#ef4444' }
];

export const getConfidenceBands = (t) => [
  { key: 'low', label: t('assistant.confidence.bands.low'), color: '#ef4444' },
  { key: 'medium', label: t('assistant.confidence.bands.medium'), color: '#f59e0b' },
  { key: 'high', label: t('assistant.confidence.bands.high'), color: '#10b981' }
];

export const getPriorityFilterOptions = (t) => [
  { value: 'P0', label: t('assistant.priority.levels.P0.label'), Icon: null, color: 'text-red-600', dot: 'bg-red-500' },
  { value: 'P1', label: t('assistant.priority.levels.P1.label'), Icon: null, color: 'text-orange-600', dot: 'bg-orange-500' },
  { value: 'P2', label: t('assistant.priority.levels.P2.label'), Icon: null, color: 'text-blue-500', dot: 'bg-blue-500' },
  { value: 'P3', label: t('assistant.priority.levels.P3.label'), Icon: null, color: 'text-emerald-600', dot: 'bg-emerald-500' }
];
